import { Adjustments } from "@/core/types"

type ParametricCurvePoints = [
	/* Type (constant) */
	number,
	/* Edges of regions */
	number,
	number,
	number,
	/* Values as offsets */
	number,
	number,
	number,
	number,
	/* Optional value? */
	number,
]

class ParametricCurve {
	/* Constants */

	L2U = 0.69314718055966295651160180568695068359375
	L2L = 0.28235290563031577122588448175013436025525412068e-12
	R_LN2 = 1.442695040888963407359924681001892137426645954152985934135449406931

	/* Parameters */

	mc = 0
	mfc = 0
	msc = 0
	mhc = 0

	x = ParametricCurve.createPoints()
	y = ParametricCurve.createPoints()

	curvePoints: Float32Array = new Float32Array(curveWidth)

	identity = true
	private numberView = new DataView(new ArrayBuffer(8)) // 8 bytes for a Float64

	constructor() {}

	static createPoints(): ParametricCurvePoints {
		return [0, 0, 0, 0, 0, 0, 0, 0, 0]
	}

	public update(p: ParametricCurvePoints) {
		if (p.at(4) != 0.0 || p.at(5) != 0.0 || p.at(6) != 0.0 || p.at(7) != 0.0) {
			this.identity = false

			this.x = ParametricCurve.createPoints()
			this.x[0] = p[0]

			for (let i = 1; i < 4; i++) {
				this.x[i] = Math.min(Math.max(p[i] || 0, 0.001), 0.99)
			}

			for (let i = 4; i < 8; i++) {
				this.x[i] = ((p[i] || 0) + 100.0) / 200.0
			}

			if (p.length < 9) {
				this.x[8] = 1.0
			} else {
				this.x[8] = p[8] / 100.0
			}

			this.mc = -this.xlog(2.0) / this.xlog(this.x[2])
			const mbase = this.pfullAlt(0.5, this.x[6], this.x[5])

			this.mfc = this.xexp(this.xlog(mbase) / this.mc)
			this.msc = -this.xlog(2.0) / this.xlog(this.x[1] / this.x[2])
			this.mhc = -this.xlog(2.0) / this.xlog((this.x[3] - this.x[2]) / (1 - this.x[2]))
		} else {
			this.identity = true
		}

		for (let i = 0; i < this.curvePoints.length; i++) {
			const x = i / (this.curvePoints.length - 1)
			const y = this.interpolate(x)
			// Scale up the curve to make it closer to LR parametric curve
			this.curvePoints[i] = Math.min(Math.max((y - x) * 1.25 + x, 0), 1)
		}

		// The above scaling and clipping will make the curve not smooth, so we need to smooth it
		this.curvePoints = this.smoothCurve(this.curvePoints)
	}

	public interpolate(t: number): number {
		if (this.identity == true) {
			return t
		}

		if (t <= 1e-14) {
			return 0.0
		}

		const tv = this.xexp(Math.max(this.mc * this.xlog(t), -236.0))
		const base = this.pfullAlt(tv, this.x[6], this.x[5])
		const stretched = this.xexp(this.xlog(base) / this.mc)

		if (t < this.x[2]) {
			// add shadows effect:
			const stv = this.xexp(Math.max(this.msc * this.xlog(stretched / this.mfc), -236.0))
			const sbase = this.pfullAlt(stv, this.x[7], 0.5)
			return this.mfc * this.xexp(this.xlog(sbase) / this.msc)
		} else {
			// add highlights effect:
			const htv = this.xexp(
				Math.max(this.mhc * this.xlog((stretched - this.mfc) / (1.0 - this.mfc)), -236.0),
			)

			if (htv < 1e-6) {
				return stretched // this part of the curve isn't affected by highlight, return the base curve
			} else {
				const hbase = this.pfullAlt(htv, 0.5, this.x[4])
				return this.mfc + (1.0 - this.mfc) * this.xexp(this.xlog(hbase) / this.mhc)
			}
		}
	}

	private xexp(d: number): number {
		const q = Math.trunc(this.xrint(d * this.R_LN2))
		let s: number, u: number

		s = this.mla(q, -this.L2U, d)
		s = this.mla(q, -this.L2L, s)

		u = 2.08860621107283687536341e-9
		u = this.mla(u, s, 2.51112930892876518610661e-8)
		u = this.mla(u, s, 2.75573911234900471893338e-7)
		u = this.mla(u, s, 2.75572362911928827629423e-6)
		u = this.mla(u, s, 2.4801587159235472998791e-5)
		u = this.mla(u, s, 0.000198412698960509205564975)
		u = this.mla(u, s, 0.00138888888889774492207962)
		u = this.mla(u, s, 0.00833333333331652721664984)
		u = this.mla(u, s, 0.0416666666666665047591422)
		u = this.mla(u, s, 0.166666666666666851703837)
		u = this.mla(u, s, 0.5)

		u = s * s * u + s + 1
		u = this.ldexpk(u, q)

		if (this.xisminf(d)) u = 0

		return u
	}

	private pfullAlt(x: number, sh: number, hl: number): number {
		const t = (1.0 - sh) * (1.0 - hl) * this.basel_alt(x) + sh * hl * this.baseu_alt(x)
		return x <= 0.5
			? t +
					(1.0 - sh) * hl * this.basel_alt(2.0 * x) * 0.5 +
					sh * (1.0 - hl) * this.baseu_alt(2.0 * x) * 0.5
			: t +
					(1.0 - sh) * hl * (0.5 + this.baseu_alt(2.0 * x - 1.0) * 0.5) +
					sh * (1.0 - hl) * (0.5 + this.basel_alt(2.0 * x - 1.0) * 0.5)
	}

	private basel_alt(x: number): number {
		return (2.0 - x) * x * x * x
	}

	private baseu_alt(x: number): number {
		return x * (2.0 + (x - 2.0) * x * x)
	}

	private xlog(d: number): number {
		const e = this.ilogbp1(d * 0.7071)
		const m = this.ldexpk(d, -e)
		let x = (m - 1) / (m + 1)
		const x2 = x * x

		let t = 0.148197055177935105296783
		t = this.mla(t, x2, 0.153108178020442575739679)
		t = this.mla(t, x2, 0.181837339521549679055568)
		t = this.mla(t, x2, 0.22222194152736701733275)
		t = this.mla(t, x2, 0.285714288030134544449368)
		t = this.mla(t, x2, 0.399999999989941956712869)
		t = this.mla(t, x2, 0.666666666666685503450651)
		t = this.mla(t, x2, 2)

		x = x * t + 0.693147180559945286226764 * e

		if (this.xispinf(d)) x = Infinity
		if (d < 0) x = NaN
		if (d == 0) x = -Infinity

		return x
	}

	private ilogbp1(d: number): number {
		const m = d < 4.9090934652977266e-91
		d = m ? 2.037035976334486e90 * d : d

		let q = Number((this.doubleToRawLongBits(d) >> 52n) & 0x7ffn)
		q = m ? q - (300 + 0x03fe) : q - 0x03fe

		return q
	}

	private xispinf(x: number): boolean {
		return x == Infinity
	}

	private xisminf(x: number) {
		return x == -Infinity
	}

	private xrint(x: number) {
		return x < 0 ? x - 0.5 : x + 0.5
	}

	private mla(x: number, y: number, z: number) {
		return x * y + z
	}

	private ldexpk(x: number, q: number) {
		let u
		let m

		m = q >> 31
		m = (((m + q) >> 9) - m) << 7
		q = q - (m << 2)

		if (!isFinite(m)) {
			return 0
		}

		u = this.longBitsToDouble(BigInt(Math.trunc(m + 0x3ff)) << 52n)

		let u2 = u * u
		u2 = u2 * u2

		x = x * u2

		if (!isFinite(q)) {
			return 0
		}

		u = this.longBitsToDouble(BigInt(Math.trunc(q + 0x3ff)) << 52n)

		return x * u
	}

	private longBitsToDouble(i: bigint): number {
		this.numberView.setBigInt64(0, i, true) // true for little-endian
		return this.numberView.getFloat64(0, true) // true for little-endian
	}

	private doubleToRawLongBits(d: number): bigint {
		this.numberView.setFloat64(0, d, true) // false for big-endian
		return this.numberView.getBigInt64(0, true).valueOf()
	}

	private smoothCurve(points: Float32Array, smoothWindow: number = 500): Float32Array {
		const halfWindow = Math.floor(smoothWindow / 2)
		const smoothRadius = Math.max(3, Math.floor(points.length / 12))

		// Find the knee where the curve lifts from 0
		let liftOffKnee = -1
		for (let i = 5; i < points.length / 2; i++) {
			if (points[i - 1] === 0 && (points[i] as number) > 0) {
				liftOffKnee = i
				break
			}
		}

		// Find the knee where the curve approaches 1
		let approachKnee = -1
		for (let i = Math.floor(points.length / 2); i < points.length - 5; i++) {
			if ((points[i - 1] as number) < 1 && (points[i] as number) === 1) {
				approachKnee = i
				break
			}
		}

		if ((liftOffKnee === -1 && approachKnee === -1) || points.length < 1) {
			return points
		}

		const smoothedPoints = new Float32Array(points)

		// 1. Pre-calculate cumulative sum for efficient window sum calculation
		const cumSum = new Float32Array(points.length)
		cumSum[0] = points[0] as number
		for (let i = 1; i < points.length; i++) {
			cumSum[i] = (cumSum[i - 1] || 0) + (points[i] as number)
		}

		// 2. Pre-calculate smoothed values for each point using the cumulative sum
		const preSmoothed = new Float32Array(points.length)
		for (let i = 0; i < points.length; i++) {
			const start = Math.max(0, i - smoothRadius)
			const end = Math.min(points.length - 1, i + smoothRadius)
			const sum = (cumSum[end] || 0) - (start > 0 ? cumSum[start - 1] || 0 : 0)
			const count = end - start + 1
			preSmoothed[i] = sum / count
		}

		// Smooth around the lift-off knee
		if (liftOffKnee !== -1) {
			const start = Math.max(0, liftOffKnee - halfWindow)
			const end = Math.min(points.length, liftOffKnee + halfWindow)
			for (let j = start; j < end; j++) {
				const distance = Math.abs(j - liftOffKnee)
				const smoothingStrength = Math.max(0, 1 - distance / halfWindow)
				const smoothedValue = preSmoothed[j] || (points[j] as number)
				const originalValue = points[j] as number
				smoothedPoints[j] =
					originalValue * (1 - smoothingStrength) + smoothedValue * smoothingStrength
			}
		}

		// Smooth around the approach knee
		if (approachKnee !== -1) {
			const start = Math.max(0, approachKnee - halfWindow)
			const end = Math.min(points.length, approachKnee + halfWindow)
			for (let j = start; j < end; j++) {
				const distance = Math.abs(j - approachKnee)
				const smoothingStrength = Math.max(0, 1 - distance / halfWindow)
				const smoothedValue = preSmoothed[j] || (points[j] as number)
				const originalValue = smoothedPoints[j] as number // Use already partially smoothed value
				smoothedPoints[j] =
					originalValue * (1 - smoothingStrength) + smoothedValue * smoothingStrength
			}
		}

		return smoothedPoints
	}
}

const curveWidth = 4096
const curve = new ParametricCurve()

export const getParametricCurvePoint = (x: number): number => {
	// return curve.interpolate(x)

	const { curvePoints } = curve
	const position = Math.min(
		Math.max(Math.round(x * (curvePoints.length - 1)), 0),
		curvePoints.length - 1,
	)
	return curvePoints[position] || 0
}

export const isIdentityParametricCurve = (values: Adjustments): boolean => {
	return (
		values.parametricHighlights === 0 &&
		values.parametricLights === 0 &&
		values.parametricDarks === 0 &&
		values.parametricShadows === 0
	)
}

export const getParametricCurvesData = (values: Adjustments) => {
	curve.update([
		/* Type (constant) */
		2,
		/* Edges of regions */
		values.parametricDarksStop,
		values.parametricMidsStop,
		values.parametricLightsStop,
		/* Values as offsets */
		values.parametricHighlights * 100,
		values.parametricLights < 0 ? values.parametricLights * 85 : values.parametricLights * 100,
		values.parametricDarks > 0 ? values.parametricDarks * 85 : values.parametricDarks * 100,
		values.parametricShadows * 100,
		100,
	])

	return curve.curvePoints
}
